import * as Icons from "lucide-react";
import { cn } from "@/lib/utils";
import type { NavItem } from "@/lib/permissions";
import { Tooltip } from "@/components/ui/tooltip";
import { isNavItemOrDescendantActive, pathMatches } from "./sidebarNavUtils";

type GetIcon = (name: string) => typeof Icons.Circle;

type Props = {
  items: NavItem[];
  depth: number;
  pathname: string;
  sidebarCollapsed: boolean;
  openIds: Set<string>;
  toggleOpenId: (id: string) => void;
  onNavigate: (path: string) => void;
  getIcon: GetIcon;
};

export function SidebarNavTree({
  items,
  depth,
  pathname,
  sidebarCollapsed,
  openIds,
  toggleOpenId,
  onNavigate,
  getIcon,
}: Props) {
  if (sidebarCollapsed && depth > 0) return null;

  return (
    <ul className={cn("space-y-1", depth > 0 && "pl-2 mt-1 ml-2 border-l-2 border-gray-100")}>
      {items.map((item) => {
        const Icon = getIcon(item.icon);
        const hasSubmenu = Boolean(item.submenu?.length);
        const isOpen = openIds.has(item.id);
        const branchActive = isNavItemOrDescendantActive(item, pathname);
        const selfActive = item.path ? pathMatches(pathname, item.path, item.exact) : false;
        const showChevron = hasSubmenu && !sidebarCollapsed;

        const handleClick = () => {
          if (hasSubmenu && !sidebarCollapsed) {
            toggleOpenId(item.id);
          } else if (item.path) {
            onNavigate(item.path);
          }
        };

        const rowClass =
          depth === 0
            ? branchActive
              ? "bg-primary text-primary-foreground shadow-sm"
              : "text-[#495057] hover:bg-[#F8F9FA] hover:text-primary"
            : selfActive
              ? "bg-primary/10 text-primary font-semibold"
              : hasSubmenu && branchActive
                ? "text-primary font-medium bg-primary/5"
                : "text-[#495057] hover:bg-[#F8F9FA] hover:text-primary";

        const button = (
          <button
            type="button"
            onClick={handleClick}
            className={cn(
              "w-full flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-200",
              depth === 0 ? "px-3 py-2.5" : "px-3 py-2 rounded-md",
              rowClass,
              hasSubmenu && !sidebarCollapsed && "justify-between"
            )}
          >
            <div className="flex items-center min-w-0 gap-3">
              <Icon className={cn("flex-shrink-0", depth === 0 ? "w-5 h-5" : "w-4 h-4")} />
              {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
            </div>
            {showChevron && (
              <Icons.ChevronDown
                className={cn(
                  "w-4 h-4 flex-shrink-0 transition-transform duration-200",
                  isOpen && "rotate-180"
                )}
              />
            )}
          </button>
        );

        return (
          <li key={item.id}>
            {sidebarCollapsed && depth === 0 ? (
              <Tooltip content={item.label} side="right" className="ml-2">
                <div className="relative">{button}</div>
              </Tooltip>
            ) : (
              <>
                {button}
                {hasSubmenu && isOpen && !sidebarCollapsed && item.submenu && (
                  <SidebarNavTree
                    items={item.submenu}
                    depth={depth + 1}
                    pathname={pathname}
                    sidebarCollapsed={sidebarCollapsed}
                    openIds={openIds}
                    toggleOpenId={toggleOpenId}
                    onNavigate={onNavigate}
                    getIcon={getIcon}
                  />
                )}
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}
